import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectComponent } from '@ng-select/ng-select';
import {
  DatePickerComponent,
  ModalBodyComponent,
  ModalComponent,
  ModalFooterAction,
  ModalFooterComponent,
  ModalHeaderComponent,
} from '@groundworks/ngx-groundworks-ui';
import {
  ApiClient,
  CreateProjectSpecialNeedRequest,
  ProjectSpecialNeedInfo,
  UpdateProjectSpecialNeedRequest,
  UserInfo,
} from '../../sdk/sdk';
import { formatUtcIsoDate } from '../../utils/iso-date';

export interface SpecialNeedEditResult {
  action: 'updated' | 'deleted';
  id: string;
}

@Component({
  selector: 'app-special-need-modal',
  imports: [
    FormsModule,
    NgSelectComponent,
    DatePickerComponent,
    ModalComponent,
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
  ],
  templateUrl: './special-need-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpecialNeedModalComponent {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly apiClient = inject(ApiClient);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = signal('');
  readonly branchId = signal('');
  readonly users = signal<UserInfo[]>([]);
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly isDeleting = signal(false);

  readonly specialNeed = signal<ProjectSpecialNeedInfo | undefined>(undefined);
  readonly isEditMode = computed(() => !!this.specialNeed());

  readonly description = signal('');
  readonly assignedTo = signal<string | undefined>(undefined);
  readonly dueDate = signal<string | undefined>(undefined);
  readonly wasCompletedOnLoad = signal(false);
  readonly showDatePicker = signal(false);
  readonly selectedDateAsDate = computed(() => {
    const iso = this.dueDate();
    return iso ? new Date(iso + 'T00:00:00') : undefined;
  });

  readonly canSave = computed(() =>
    !this.wasCompletedOnLoad()
    && this.description().trim().length > 0
    && !!this.assignedTo()
    && !!this.dueDate()
    && !this.isSaving()
    && !this.isDeleting()
  );

  readonly footerActions = computed<ModalFooterAction[]>(() => {
    const saving = this.isSaving();
    const deleting = this.isDeleting();
    const canSave = this.canSave();
    const completed = this.wasCompletedOnLoad();

    if (!this.isEditMode()) {
      return [
        {
          label: 'Save',
          type: 'primary',
          action: () => this.save(),
          loading: saving,
          disabled: !canSave,
        },
      ];
    }

    return [
      {
        label: 'Delete',
        type: 'outline-danger',
        action: () => this.delete(),
        loading: deleting,
        disabled: saving || deleting,
      },
      {
        label: 'Update',
        type: 'primary',
        action: () => this.update(),
        loading: saving,
        disabled: !canSave || completed,
      },
    ];
  });

  loadUsers(): void {
    this.isLoading.set(true);
    this.apiClient.getActiveUsers(this.branchId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (users) => {
          this.users.set(users);
          this.isLoading.set(false);
        },
        error: () => this.isLoading.set(false),
      });
  }

  loadNeed(): void {
    const need = this.specialNeed();
    if (!need) return;
    this.description.set(need.details ?? '');
    this.assignedTo.set(need.assignedTo);
    this.dueDate.set(need.dueDate ? formatUtcIsoDate(need.dueDate) : undefined);
    this.wasCompletedOnLoad.set(need.isCompleted ?? false);
    this.showDatePicker.set(false);
  }

  toggleDatePicker(): void {
    this.showDatePicker.update(v => !v);
  }

  handleDateSelected(date: Date | null): void {
    if (date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      this.dueDate.set(`${y}-${m}-${d}`);
    } else {
      this.dueDate.set(undefined);
    }
    this.showDatePicker.set(false);
  }

  formatDateForDisplay(iso: string | undefined): string {
    if (!iso) return '';
    const [year, month, day] = iso.split('-');
    if (!year || !month || !day) return iso;
    return `${month}/${day}/${year}`;
  }

  save(): void {
    if (!this.canSave()) return;
    this.isSaving.set(true);

    const request = new CreateProjectSpecialNeedRequest({
      details: this.description(),
      assignedTo: this.assignedTo(),
      dueDate: this.dueDate() ? new Date(this.dueDate()! + 'T00:00:00') : undefined,
    });

    this.apiClient.createProjectSpecialNeed(this.projectId(), request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.isSaving.set(false);
          this.activeModal.close(result);
        },
        error: () => this.isSaving.set(false),
      });
  }

  update(): void {
    if (!this.canSave()) return;
    const need = this.specialNeed();
    if (!need) return;

    this.isSaving.set(true);

    const request = new UpdateProjectSpecialNeedRequest({
      details: this.description(),
      assignedTo: this.assignedTo(),
      clearAssignedTo: !this.assignedTo() && !!need.assignedTo,
      dueDate: this.dueDate() ? new Date(this.dueDate()! + 'T00:00:00') : undefined,
      clearDueDate: !this.dueDate() && !!need.dueDate,
      isCompleted: undefined,
    });

    this.apiClient.updateProjectSpecialNeed(this.projectId(), need.id, request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.activeModal.close({ action: 'updated', id: need.id } as SpecialNeedEditResult);
        },
        error: () => this.isSaving.set(false),
      });
  }

  delete(): void {
    const need = this.specialNeed();
    if (!need) return;

    this.isDeleting.set(true);

    this.apiClient.deleteProjectSpecialNeed(this.projectId(), need.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.activeModal.close({ action: 'deleted', id: need.id } as SpecialNeedEditResult);
        },
        error: () => this.isDeleting.set(false),
      });
  }

  cancel(): void {
    this.activeModal.dismiss();
  }
}
